import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, createTextVNode as _createTextVNode, withKeys as _withKeys, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-70da3504"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "drop-down" }
const _hoisted_2 = { class: "em-dropdown" }
const _hoisted_3 = ["id", "aria-label", "aria-expanded"]
const _hoisted_4 = { class: "em-dropdown__list-container" }
const _hoisted_5 = ["aria-activedescendant", "aria-label"]
const _hoisted_6 = ["id", "aria-selected", "tabindex", "onClick", "onKeydown"]

import '@aab/sc-aab-icon';
import { DOWN_ARROW_KEY_CODE, ENTER_KEY_CODE, ESCAPE_KEY_CODE, SPACE_BAR_KEY_CODE } from './constants/KeyCodes';
import type { DropDownOption } from './models/DropDownOption';
import { reactive } from 'vue';
import { sy_arrow_chevron_down as syArrowChevronDown } from '@aab/sc-aab-icon-set';


export default /*#__PURE__*/_defineComponent({
  __name: 'DropDown',
  props: {
    options: null,
    extendedOptions: null,
    defaultOption: null,
    label: { default: '' },
    id: null
  },
  emits: ['selectionChanged'],
  setup(__props: any, { emit }) {

const props = __props as { readonly options: ReadonlyArray<DropDownOption>, readonly extendedOptions: ReadonlyArray<DropDownOption>, readonly defaultOption: DropDownOption['value'], label: string, readonly id: string };





const state = reactive({
  displayedOptions: [...props.options],
  selectedOption: props.options.find((item) => item.value === props.defaultOption),
  isDropdownOpen: false
});

function toggleListVisibility(event: KeyboardEvent): void | never {
  if (event.keyCode === ESCAPE_KEY_CODE) return closeList();

  if (event.keyCode === DOWN_ARROW_KEY_CODE) {
    // avoid default effect
    event.preventDefault();
    state.isDropdownOpen = true;
    if (!state.selectedOption) return;
    const selector = getOptionId(props.id, state.selectedOption.value);
    const option: HTMLElement | null = document.querySelector(`#${selector}`);
    if (!option) throw new Error(`Cannot toggle visibility of selectedOption "${selector}"`);
    option.focus();
    return;
  }

  const isDropDownOpen = SPACE_BAR_KEY_CODE.includes(event.keyCode) || event.keyCode === ENTER_KEY_CODE;
  if (isDropDownOpen) {
    // avoid space key to do unnecessary scroll
    if (SPACE_BAR_KEY_CODE.includes(event.keyCode)) event.preventDefault();

    state.isDropdownOpen = !state.isDropdownOpen;
    if (state.isDropdownOpen) {
      if (!state.selectedOption) return;
      const selector = getOptionId(props.id, state.selectedOption.value);
      const option: HTMLElement | null = document.querySelector(`#${selector}`);
      if (!option) throw new Error(`Cannot toggle visibility of selectedOption "${selector}"`);
      option.focus();
    }
    return;
  }
  if (event.type === 'click') {
    if (state.isDropdownOpen) {
      closeList();
    } else {
      state.isDropdownOpen = true;
    }
  }
}

function getOptionId(id: string, value: string): string {
  return `item__${id}__${value}`;
}

function closeList(): void | never {
  state.isDropdownOpen = false;
  if (state.selectedOption?.value) state.displayedOptions = [...props.options];

  const list: HTMLElement | null = document.getElementById(props.id);
  if (!list) throw new Error(`Cannot close list "${props.id}"`);
  list.focus();
}

function setOption(option: DropDownOption): void {
  if (option.value !== 'more-types') {
    state.selectedOption = option;
    emit('selectionChanged', { detail: { value: option.value } });
    closeList();
  } else {
    state.displayedOptions = [...props.extendedOptions];
  }
}

function isSelectedOption(value: DropDownOption['value']): boolean {
  return value === state.selectedOption?.value;
}

function getSelectedValue(): string | undefined {
  return props.extendedOptions.find((option) => option.value === state.selectedOption?.value)?.name;
}

function focusNextListItem(currentActiveElementIndex: number, option: DropDownOption): void {
  const isCurrentActiveElementIsNotLastItem = currentActiveElementIndex < state.displayedOptions.length - 1;
  if (isCurrentActiveElementIsNotLastItem) {
    const nextOption = state.displayedOptions[state.displayedOptions.findIndex((o) => o.value === option.value) + 1];
    const nextOptionSelector = getOptionId(props.id, nextOption.value);
    const item: HTMLElement | null = document.querySelector(`#${nextOptionSelector}`);
    if (!item) throw new Error(`Cannot focus next item "${nextOptionSelector}"`);
    item.focus();
  }
}

function focusPreviousListItem(currentActiveElementIndex: number, option: DropDownOption): void {
  const isCurrentActiveElementIsNotLastItem = currentActiveElementIndex > 0;
  if (isCurrentActiveElementIsNotLastItem) {
    const nextOption = state.displayedOptions[state.displayedOptions.findIndex((o) => o.value === option.value) - 1];
    const nextOptionSelector = getOptionId(props.id, nextOption.value);
    const item: HTMLElement | null = document.querySelector(`#${nextOptionSelector}`);
    if (!item) throw new Error(`Cannot focus next item "${nextOptionSelector}"`);
    item.focus();
  }
}

function navListItems(index: number): void {
  if (index === state.displayedOptions.length - 1) closeList();
}

function focusSelectedItem(event: Event): void {
  if (state.isDropdownOpen) {
    event.preventDefault();
    if (!state.selectedOption) return;
    const selector = getOptionId(props.id, state.selectedOption.value);
    const option: HTMLElement | null = document.querySelector(`#${selector}`);
    if (!option) throw new Error(`Cannot focus selectedItem "${selector}"`);
    option.focus();
  }
}

return (_ctx: any,_cache: any) => {
  const _component_aab_icon = _resolveComponent("aab-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        id: __props.id,
        role: "button",
        "aria-haspopup": "listbox",
        "aria-label": __props.label,
        "aria-expanded": state.isDropdownOpen,
        class: _normalizeClass(["em-dropdown__selected", { expanded: state.isDropdownOpen }]),
        tabindex: "0",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleListVisibility($event))),
        onKeydown: [
          _cache[1] || (_cache[1] = ($event: any) => (toggleListVisibility($event))),
          _cache[2] || (_cache[2] = _withKeys(($event: any) => (focusSelectedItem($event)), ["tab"]))
        ]
      }, [
        _createTextVNode(_toDisplayString(getSelectedValue()) + " ", 1),
        _createVNode(_component_aab_icon, {
          class: _normalizeClass(["em-dropdown__arrow", { expanded: state.isDropdownOpen }]),
          svg: _unref(syArrowChevronDown),
          size: "3"
        }, null, 8, ["class", "svg"])
      ], 42, _hoisted_3),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("ul", {
          class: _normalizeClass(["em-dropdown__list", { open: state.isDropdownOpen }]),
          role: "listbox",
          "aria-activedescendant": state.selectedOption ? state.selectedOption.value : '',
          "aria-label": __props.label
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.displayedOptions, (item, key) => {
            return (_openBlock(), _createElementBlock("li", {
              id: getOptionId(__props.id, item.value),
              key: key,
              ref_for: true,
              ref: 'em-dropdown-list-item-' + key,
              "aria-selected": isSelectedOption(item.value),
              class: _normalizeClass(["em-dropdown__list-item", { 'see-more': item.value === 'more-types', selected: isSelectedOption(item.value) }]),
              tabindex: state.isDropdownOpen ? 0 : -1,
              role: "option",
              onClick: ($event: any) => (setOption(item)),
              onKeydown: [
                _withKeys(($event: any) => (setOption(item)), ["enter"]),
                _withKeys(_withModifiers(($event: any) => (setOption(item)), ["prevent"]), ["space"]),
                _withKeys(_withModifiers(($event: any) => (focusNextListItem(key, item)), ["prevent"]), ["down"]),
                _withKeys(_withModifiers(($event: any) => (focusPreviousListItem(key, item)), ["prevent"]), ["up"]),
                _withKeys(($event: any) => (navListItems(key)), ["tab"]),
                _withKeys(closeList, ["esc"])
              ]
            }, [
              _createTextVNode(_toDisplayString(item.name) + " ", 1),
              (item.value === 'more-types')
                ? (_openBlock(), _createBlock(_component_aab_icon, {
                    key: 0,
                    class: "em-dropdown__arrow",
                    svg: _unref(syArrowChevronDown),
                    size: "3"
                  }, null, 8, ["svg"]))
                : _createCommentVNode("", true)
            ], 42, _hoisted_6))
          }), 128))
        ], 10, _hoisted_5)
      ])
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["em-dropdown__outer", { visible: state.isDropdownOpen }]),
      onClick: closeList
    }, null, 2)
  ]))
}
}

})